/**
 * CPUParticles2D parser: the Node2D transform and the emitter properties. The
 * parameter slots come from one table, as they differ only in their serialised
 * prefix and (for Scale) their default, and Godot stores them as parallel arrays.
 */
import { parseNode2D } from '../../base/node2d/parser';
import { boolOr, enumOr, floatOr, intOr, vec2Or } from '../../../parser/valueParsers';
import { parsePackedVector2Array } from '../../../resources/shapes/packedArray';
import { colorOr } from '../../../utils/colorParser';
import { CPUParticles2DDrawOrder, CPUParticles2DEmissionShape } from '../../../godot/scene/particles2d';
import { parseEmissionPoints, parseParticleAmount, parseParticleParams, } from '../../shared/cpuparticles/params';
export function parseCPUParticles2D(heading, properties) {
    const base = parseNode2D(heading, properties);
    const context = base.name || 'CPUParticles2D';
    const result = {
        ...base,
        emitting: boolOr(properties.emitting, true, context),
        amount: parseParticleAmount(properties.amount, `${context}.amount`),
        lifetime: floatOr(properties.lifetime, 1, `${context}.lifetime`),
        one_shot: boolOr(properties.one_shot, false, context),
        preprocess: floatOr(properties.preprocess, 0, `${context}.preprocess`),
        speed_scale: floatOr(properties.speed_scale, 1, `${context}.speed_scale`),
        explosiveness: floatOr(properties.explosiveness, 0, `${context}.explosiveness`),
        randomness: floatOr(properties.randomness, 0, `${context}.randomness`),
        use_fixed_seed: boolOr(properties.use_fixed_seed, false, context),
        // cpu_particles_2d.h:261: the setter takes uint32_t, so 4294967295 is the
        // seed the file states, not -1.
        seed: intOr(properties.seed, 0, `${context}.seed`, 'uint32'),
        lifetime_randomness: floatOr(properties.lifetime_randomness, 0, `${context}.lifetime_randomness`),
        fixed_fps: intOr(properties.fixed_fps, 0, `${context}.fixed_fps`),
        fract_delta: boolOr(properties.fract_delta, true, context),
        local_coords: boolOr(properties.local_coords, false, context),
        draw_order: enumOr(properties.draw_order, CPUParticles2DDrawOrder.INDEX, [CPUParticles2DDrawOrder.INDEX, CPUParticles2DDrawOrder.LIFETIME], `${context}.draw_order`),
        emission_shape: enumOr(properties.emission_shape, CPUParticles2DEmissionShape.POINT, [
            CPUParticles2DEmissionShape.POINT,
            CPUParticles2DEmissionShape.SPHERE,
            CPUParticles2DEmissionShape.SPHERE_SURFACE,
            CPUParticles2DEmissionShape.RECTANGLE,
            CPUParticles2DEmissionShape.POINTS,
            CPUParticles2DEmissionShape.DIRECTED_POINTS,
            CPUParticles2DEmissionShape.RING,
        ], `${context}.emission_shape`),
        emission_sphere_radius: floatOr(properties.emission_sphere_radius, 1, context),
        emission_rect_extents: vec2Or(properties.emission_rect_extents, { x: 1, y: 1 }, context),
        emission_ring_radius: floatOr(properties.emission_ring_radius, 1, context),
        emission_ring_inner_radius: floatOr(properties.emission_ring_inner_radius, 0.8, context),
        ...emissionArrays(properties, context),
        particle_flag_align_y: boolOr(properties.particle_flag_align_y, false, context),
        direction: vec2Or(properties.direction, { x: 1, y: 0 }, `${context}.direction`),
        spread: floatOr(properties.spread, 45, `${context}.spread`),
        gravity: vec2Or(properties.gravity, { x: 0, y: 980 }, `${context}.gravity`),
        params: parseParticleParams(properties),
        split_scale: boolOr(properties.split_scale, false, context),
        color: colorOr(properties.color, { r: 1, g: 1, b: 1, a: 1 }),
    };
    if (properties.texture)
        result.texture = properties.texture;
    if (properties.scale_curve_x)
        result.scale_curve_x = properties.scale_curve_x;
    if (properties.scale_curve_y)
        result.scale_curve_y = properties.scale_curve_y;
    if (properties.color_ramp)
        result.color_ramp = properties.color_ramp;
    if (properties.color_initial_ramp)
        result.color_initial_ramp = properties.color_initial_ramp;
    return result;
}
function emissionArrays(properties, context) {
    const { points, normals, colors } = parseEmissionPoints({
        emission_points: properties.emission_points,
        emission_normals: properties.emission_normals,
        emission_colors: properties.emission_colors,
    }, parsePackedVector2Array, (key) => `${context}.${key}`);
    return { emission_points: points, emission_normals: normals, emission_colors: colors };
}
//# sourceMappingURL=parser.js.map